import React, { useEffect, useState } from "react";
import {
  extractBoldTitleFromMarkdown,
  extractShortMessageFromProgress,
  extractTokenFromTransfer,
  getStepInfo
} from "./utils";
import { extractLinksFromResearch } from "./ResearchPills";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import { ActiveTag, ChatTimelineThinkingMode } from "../types";
import { useSelector } from "react-redux";

interface ThinkingIndicatorProps {
  currentStep: ActiveTag | null;
  activeStep: ActiveTag | null;
  isLastMessage: boolean;
  isProcessExpanded: boolean;
  streaming: boolean;
  hasPMTags: boolean;
}

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({
  currentStep,
  activeStep,
  isLastMessage,
  isProcessExpanded,
  streaming,
  hasPMTags
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [delayedHeadline, setDelayedHeadline] = useState('');

  const {
    thinkingMode
  } = useSelector((state: any) => state.chatMessages);

  useEffect(() => {
    if (thinkingMode === ChatTimelineThinkingMode.OFF) {
      const targetHeadline = getDynamicHeadline();
      const timeout = setTimeout(() => {
        setDelayedHeadline(targetHeadline);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setDelayedHeadline(getStaticHeadline());
    }
  }, [thinkingMode, activeStep?.type, activeStep?.content]);

  useEffect(() => {
    let intervalId: string | number | NodeJS.Timer | undefined;

    if (startTime) {
      intervalId = setInterval(() => {
        const currentElapsed = Math.floor((Date.now() - startTime) / 1000);
        setElapsedTime(currentElapsed);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [startTime]);

  useEffect(() => {
    if (streaming && !startTime) {
      setStartTime(Date.now());
      setElapsedTime(0);
    }

    // Stop timer when streaming ends or PM tags are detected
    if ((!streaming && startTime) || hasPMTags) {
      setStartTime(null);
    }
  }, [streaming, hasPMTags]);

  const stepInfo = getStepInfo(activeStep?.type);
  const isCurrentlyStreaming = activeStep && currentStep && activeStep === currentStep;
  const isCompleted = !isCurrentlyStreaming;

  const researchIcons = React.useMemo(() => {
    if (activeStep?.type !== 'research') return [] as string[];
    const links = extractLinksFromResearch(activeStep?.content || '') || [];
    const favs: string[] = [];
    for (let i = 0; i < links.length && favs.length < 4; i++) {
      const l = links[i];
      let host = '';
      try { host = new URL(l.url).hostname; } catch { }
      const favicon = l.favicon || (host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : '');
      if (favicon) favs.push(favicon);
    }
    return favs;
  }, [activeStep?.type, activeStep?.content]);

  const getDynamicHeadline = () => {
    if (activeStep?.text) return activeStep.text;
    if (activeStep?.type === 'thinking') {
      return extractBoldTitleFromMarkdown(activeStep?.content);
    } else if (activeStep?.type === 'research') {
      return 'Web Search';
    } else if (activeStep?.type === 'progress') {
      return extractShortMessageFromProgress(activeStep?.content);
    } else if (activeStep?.type === 'transfer') {
      return extractTokenFromTransfer(activeStep?.content);
    } else if (activeStep?.type === 'summarization') {
      return extractTokenFromTransfer(activeStep?.content) || 'Summarizing data';
    } else {
      return stepInfo.label;
    }
  };

  const getStaticHeadline = () => {
    if (activeStep?.text) return activeStep.text;
    if (activeStep?.type === 'thinking') {
      return 'Thinking';
    } else if (activeStep?.type === 'research') {
      return 'Web Search';
    } else if (activeStep?.type === 'progress') {
      return 'Processing';
    } else if (activeStep?.type === 'transfer') {
      return 'Transfer';
    } else if (activeStep?.type === 'summarization') {
      return 'Summarizing data';
    } else {
      return stepInfo.label;
    }
  };

  return (
    <div className="flex items-center group/indicator relative -mt-1.5">
      <span
        className={`
          ${!streaming ? "block" : "hidden"} 
          ${isLastMessage && streaming ? "group-hover/indicator:block" : ""} 
          ${!isLastMessage ? "!block" : ""}
        `}
        title={isProcessExpanded ? "Collapse thinking" : "Expand thinking"}
      >
        {isProcessExpanded ? (
          <HiChevronDown className="chevron-icon" />
        ) : (
          <HiChevronRight className="chevron-icon" />
        )}
      </span>

      {streaming && isLastMessage ? (
        <>
          {React.createElement(stepInfo.icon, {
            className: `chevron-icon block ${streaming ? "group-hover/indicator:hidden" : ""}`,
            style: { color: stepInfo.color }
          })}
          <span className="ml-1 thinking-text-glow">{delayedHeadline}</span>
          {activeStep?.type === 'research' && researchIcons.length > 0 && (
            <span className="ml-2 flex items-center -space-x-1">
              {researchIcons.map((src, idx) => (
                <img key={`${src}-${idx}`} src={src} alt="" className="w-5 h-5 rounded-full" />
              ))}
            </span>
          )}
          {!isCompleted && (
            <div className="thinking-dots">
              <div className="thinking-dot"></div>
              <div className="thinking-dot"></div>
              <div className="thinking-dot"></div>
            </div>
          )}
        </>
      ) : (
        <span>{isLastMessage && elapsedTime ? `Thought for ${elapsedTime}s` : "Thoughts"}</span>
      )}
    </div>
  );
};

export default ThinkingIndicator;